import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useI18n, LANGS } from "../lib/i18n";
import {
  Phone, Copy, Loader2, Check, AlertTriangle, ShieldCheck, ShieldQuestion,
  Globe, Lock, Layers, Info, X, Languages,
} from "lucide-react";

const VIS_OPTIONS = [
  { id: "private",     Icon: Lock,   label: "Private",     blurb: "Only you can see your messages. Admin cannot see them unless you grant explicit consent." },
  { id: "hybrid",      Icon: Layers, label: "Hybrid",      blurb: "Your messages are yours; the agency admin can see all your threads for oversight." },
  { id: "transparent", Icon: Globe,  label: "Transparent", blurb: "All messages visible to every workspace member — full team collaboration." },
];

export default function SettingsProfile() {
  return (
    <Layout title="Profile & Privacy" subtitle="Your language, professional number, inbox visibility, and consent settings">
      <LanguageCard />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <NumberCard />
        <VisibilityCard />
      </div>
      <PendingConsents />
      <GDPRDisclaimer />
    </Layout>
  );
}

// ─────────── Language card ───────────
function LanguageCard() {
  const { lang, setLang, t } = useI18n();
  const [saving, setSaving] = useState(false);
  const pick = async (code) => {
    if (code === lang || saving) return;
    setSaving(true);
    try { await setLang(code); } finally { setSaving(false); }
  };
  return (
    <div className="pm-card overflow-hidden mb-6" data-testid="language-card">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-sky-50 text-sky-700 flex items-center justify-center"><Languages size={16} /></div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Language · Taal</div>
          <div className="font-display text-lg font-bold tracking-tight">{t("Taal van de app")}</div>
        </div>
        {saving && <Loader2 size={14} className="animate-spin text-slate-400" />}
      </div>
      <div className="p-4 flex flex-wrap items-center gap-2">
        {LANGS.map((l) => (
          <button key={l.code} onClick={() => pick(l.code)} disabled={saving} data-testid={`lang-${l.code}`}
            className={`h-10 px-4 rounded-full border-2 text-sm font-bold flex items-center gap-2 transition-colors ${lang === l.code ? "border-emerald-500 bg-emerald-50 text-slate-900" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
            {lang === l.code && <Check size={13} className="text-emerald-700" />} {l.label}
          </button>
        ))}
        <span className="text-[11px] text-slate-500 ml-1" data-testid="lang-hint">{t("Direct toegepast op alle schermen en opgeslagen op je account. Het zijmenu blijft Engels.")}</span>
      </div>
    </div>
  );
}

// ─────────── Twilio number card ───────────
function NumberCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [provisioning, setProvisioning] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/me/twilio-number")
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Failed to load number"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const provision = async () => {
    setProvisioning(true);
    setError(null);
    try {
      const { data: r } = await api.post("/me/twilio-number/provision", {});
      setData((d) => ({ ...(d || {}), number: r.twilio_number, sid: r.twilio_number_sid, provisioned_at: r.twilio_provisioned_at, stub: r.stub }));
    } catch (e) {
      setError(e?.response?.data?.detail || "Provisioning failed");
    } finally {
      setProvisioning(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data?.number || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) { /* noop */ }
  };

  return (
    <div className="pm-card overflow-hidden" data-testid="number-card">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
          <Phone size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Your professional number</div>
          <div className="font-display text-lg font-bold tracking-tight">Playmaker · Twilio line</div>
        </div>
      </div>

      <div className="p-5">
        {loading ? (
          <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading…</div>
        ) : data?.number ? (
          <>
            <div className="flex items-center gap-3 mb-3">
              <div className="font-display text-2xl font-black tracking-tight text-slate-900" data-testid="my-number">
                {data.number}
              </div>
              {data.stub && (
                <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wider" data-testid="number-stub-badge">
                  Stub
                </span>
              )}
            </div>
            <div className="text-[12px] text-slate-600 mb-4">
              This is your Playmaker number. Give it to clients and clubs — every SMS or WhatsApp message lands in your inbox automatically.
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={copy}
                data-testid="copy-number-btn"
                className="pm-btn-secondary h-9 px-3 rounded-lg text-[12px] font-bold flex items-center gap-1.5"
              >
                {copied ? <Check size={11} className="text-emerald-700" /> : <Copy size={11} />}
                {copied ? "Copied" : "Copy"}
              </button>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`Hi — this is my Playmaker number: ${data.number}. Reach me here for anything client-related.`)}`}
                target="_blank" rel="noreferrer"
                data-testid="share-whatsapp-btn"
                className="pm-btn-secondary h-9 px-3 rounded-lg text-[12px] font-bold flex items-center gap-1.5"
              >
                Share via WhatsApp
              </a>
              <a
                href={`mailto:?subject=My Playmaker number&body=${encodeURIComponent(`Hi — this is my Playmaker number: ${data.number}.`)}`}
                data-testid="share-email-btn"
                className="pm-btn-secondary h-9 px-3 rounded-lg text-[12px] font-bold flex items-center gap-1.5"
              >
                Share via email
              </a>
            </div>
            {data.stub && (
              <div className="mt-4 text-[11px] text-slate-500 bg-amber-50 border border-amber-200 rounded-lg p-2.5 flex items-start gap-1.5">
                <Info size={11} className="mt-0.5 shrink-0 text-amber-700" />
                <span>This is a placeholder number for preview/testing. Real Twilio purchase is disabled until <code className="bg-white px-1 rounded">PLAYMAKER_ALLOW_REAL_NUMBER_PURCHASE=true</code> is set on production.</span>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="text-sm text-slate-600 mb-4 leading-snug">
              Your Playmaker number is your professional identity. Give it to clients and clubs — all conversations are logged automatically so nothing gets lost. Your personal number stays private.
            </div>
            <button
              onClick={provision}
              disabled={provisioning}
              data-testid="provision-number-btn"
              className="pm-btn-primary h-11 px-4 rounded-xl text-sm font-black flex items-center gap-2 disabled:opacity-50"
            >
              {provisioning ? <Loader2 size={13} className="animate-spin" /> : <Phone size={13} />}
              {provisioning ? "Provisioning…" : "Get my number"}
            </button>
            {!data?.real_purchase_enabled && (
              <div className="mt-3 text-[11px] text-slate-500 flex items-start gap-1.5">
                <Info size={11} className="mt-0.5 shrink-0" />
                Preview mode: a placeholder number will be assigned. Real Twilio purchase happens after deploy.
              </div>
            )}
          </>
        )}
        {error && <div className="mt-3 text-[12px] text-rose-700 flex items-center gap-1.5"><AlertTriangle size={11} /> {error}</div>}
      </div>
    </div>
  );
}

// ─────────── Inbox visibility card ───────────
function VisibilityCard() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = () => {
    api.get("/me/inbox-visibility")
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Failed to load"));
  };
  useEffect(() => { load(); }, []);

  const save = async (mode) => {
    if (mode === data?.mode) return;
    setSaving(true);
    setError(null);
    const prev = data?.mode;
    setData((d) => ({ ...d, mode }));
    try {
      await api.put("/me/inbox-visibility", { mode });
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save");
      setData((d) => ({ ...d, mode: prev }));
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div className="pm-card p-5 text-sm text-slate-500 flex items-center gap-2" data-testid="my-visibility-card-loading"><Loader2 size={12} className="animate-spin" /> Loading…</div>;

  return (
    <div className="pm-card overflow-hidden" data-testid="my-visibility-card">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-700 flex items-center justify-center">
          {data.mode === "private" ? <Lock size={16} /> : data.mode === "transparent" ? <Globe size={16} /> : <Layers size={16} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Your inbox visibility</div>
          <div className="font-display text-lg font-bold tracking-tight">in {data.workspace_name}</div>
        </div>
        {saving && <Loader2 size={14} className="animate-spin text-slate-400" />}
      </div>
      <div className="p-4 space-y-2.5">
        {VIS_OPTIONS.map((o) => {
          const Icon = o.Icon;
          const active = data.mode === o.id;
          const isRec = data.recommended === o.id;
          return (
            <button
              key={o.id}
              onClick={() => save(o.id)}
              disabled={saving}
              data-testid={`my-vis-${o.id}`}
              className={`w-full text-left p-3 rounded-xl border-2 transition-all flex items-start gap-2.5 relative ${
                active ? "border-emerald-500 bg-emerald-50/40 shadow-sm" : "border-slate-200 hover:border-slate-300 bg-white"
              }`}
            >
              {isRec && (
                <span className="absolute top-2 right-2 px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-[9px] font-bold uppercase tracking-wider">
                  Recommended
                </span>
              )}
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                <Icon size={14} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-sm font-bold">{o.label}</div>
                <div className="text-[11px] text-slate-600 mt-0.5 leading-tight">{o.blurb}</div>
              </div>
              {active && <Check size={14} className="text-emerald-700 shrink-0 mt-1.5" />}
            </button>
          );
        })}
        {data.consent_given && data.consent_given_at && (
          <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 flex items-start gap-1.5" data-testid="consent-given-note">
            <ShieldCheck size={11} className="mt-0.5 shrink-0" />
            <span>You granted admin access on {new Date(data.consent_given_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}. Switch back to Private to revoke immediately.</span>
          </div>
        )}
        {error && <div className="text-[12px] text-rose-700 flex items-center gap-1.5"><AlertTriangle size={11} /> {error}</div>}
      </div>
    </div>
  );
}

// ─────────── Pending consent requests ───────────
function PendingConsents() {
  const [pending, setPending] = useState([]);
  const [acting, setActing] = useState(null);

  const load = () => api.get("/me/inbox-consent/pending").then((r) => setPending(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const respond = async (workspace_id, decision) => {
    setActing(workspace_id + decision);
    try {
      await api.post("/me/inbox-consent/respond", { workspace_id, decision });
      load();
    } finally {
      setActing(null);
    }
  };

  if (!pending.length) return null;

  return (
    <div className="pm-card overflow-hidden mt-6 border-amber-300 bg-amber-50/30" data-testid="pending-consents-card">
      <div className="px-5 py-3 border-b border-amber-200 flex items-center gap-2">
        <ShieldQuestion size={14} className="text-amber-700" />
        <div className="font-display text-base font-bold tracking-tight">{pending.length} pending inbox-access request{pending.length === 1 ? "" : "s"}</div>
      </div>
      <div className="divide-y divide-amber-100">
        {pending.map((p) => (
          <div key={p.workspace_id} className="p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`pending-consent-${p.workspace_id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-slate-900">{p.workspace_name}</div>
              <div className="text-[11px] text-slate-600 mt-0.5">
                <b>{p.requested_by?.name || "Admin"}</b> is requesting access to your inbox for compliance and oversight. Granting changes your mode to Hybrid; you can revoke any time.
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => respond(p.workspace_id, "deny")}
                disabled={!!acting}
                data-testid={`consent-deny-${p.workspace_id}`}
                className="pm-btn-secondary h-9 px-3 rounded-lg text-[12px] font-bold flex items-center gap-1.5"
              >
                <X size={11} /> Decline
              </button>
              <button
                onClick={() => respond(p.workspace_id, "grant")}
                disabled={!!acting}
                data-testid={`consent-grant-${p.workspace_id}`}
                className="pm-btn-primary h-9 px-3 rounded-lg text-[12px] font-black flex items-center gap-1.5 disabled:opacity-50"
              >
                {acting === p.workspace_id + "grant" ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
                Grant access
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────── GDPR footer ───────────
function GDPRDisclaimer() {
  return (
    <div className="mt-6 p-4 rounded-xl bg-slate-50 border border-slate-200 text-[11px] text-slate-600 leading-snug flex items-start gap-2" data-testid="gdpr-disclaimer">
      <Info size={11} className="mt-0.5 shrink-0 text-slate-500" />
      <div>
        <b>GDPR · Article 5</b> — Your inbox visibility settings are protected under GDPR. Your employer or agency admin cannot access your professional communications without your explicit consent. You can change or revoke this at any time from this page. All consent decisions are logged for compliance.
      </div>
    </div>
  );
}
